// «Страховка» в настройках: правила без ИИ, которые каждые 10 минут следят за деньгами и лидами.
// Пороги меняет только владелец; изменения сохраняются сразу. Ниже — последние срабатывания.
// Этап 5: автостоп — правило само ставит объект на паузу в Meta (когда включена запись в Meta).
import { Card, Chip, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pause, ShieldCheck } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { api } from '../api';

/** Включена ли запись в Meta — без неё автостоп только ждёт (снимок политики из «Автономии»). */
interface WriteState {
  write: { enabled: boolean; token: boolean };
}

interface GuardConfig {
  spendNoLeads: {
    enabled: boolean;
    minSpend: number;
    cplMultiple: number;
    stop: boolean;
    stopMinSpend: number;
    stopCplMultiple: number;
  };
  cplSpike: { enabled: boolean; multiple: number; minSpend: number; stop: boolean };
  silence: { enabled: boolean; hours: number; minSpend: number; stop: boolean };
  approveDrop: { enabled: boolean; ratio: number; minDecided: number };
  disapproved: { enabled: boolean };
  cplAboveBreakEven: { enabled: boolean; multiple: number; minSpend: number };
  autoStop: { enabled: boolean; agentAfterHours: number };
}

interface GuardView {
  config: GuardConfig;
  defaults: GuardConfig;
  events: Array<{
    id: number;
    rule: string;
    severity: 'info' | 'warning' | 'critical';
    title: string;
    body: string | null;
    createdAt: string;
    resolvedAt: string | null;
  }>;
}

type Rule = keyof GuardConfig;

/** Число в тексте правила: узкое поле, сохраняется при выходе из поля. */
function Num({
  value,
  onSave,
  label,
  min = 0,
  step = 1,
  width = 5,
}: {
  value: number;
  onSave: (n: number) => void;
  label: string;
  min?: number;
  step?: number;
  width?: number;
}) {
  const [v, setV] = useState(String(value));
  useEffect(() => setV(String(value)), [value]);
  const commit = () => {
    const n = Number(v.replace(',', '.'));
    if (!Number.isFinite(n) || n < min) return setV(String(value));
    if (n !== value) onSave(n);
  };
  return (
    <input
      className="input guard-num"
      style={{ width: `${width}ch` }}
      inputMode="decimal"
      aria-label={label}
      value={v}
      step={step}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

export function GuardCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['guard'], queryFn: () => api<GuardView>('/api/guard') });
  const c = q.data?.config;

  const save = async (next: GuardConfig) => {
    qc.setQueryData<GuardView>(['guard'], (old) => (old ? { ...old, config: next } : old));
    try {
      qc.setQueryData(['guard'], await api<GuardView>('/api/guard', { config: next }, 'PUT'));
      toast('Страховка сохранена');
    } catch (e) {
      toast((e as Error).message);
      void q.refetch();
    }
  };
  const set = <R extends Rule>(rule: R, patch: Partial<GuardConfig[R]>) => {
    if (c) void save({ ...c, [rule]: { ...c[rule], ...patch } });
  };

  const policy = useQuery({
    queryKey: ['policy'],
    queryFn: () => api<WriteState>('/api/agent/policy'),
  });
  const writeOn = policy.data ? policy.data.write.enabled && policy.data.write.token : null;

  const row = (rule: Rule, title: string, text: ReactNode, stop?: ReactNode) =>
    c && (
      <li className="guard-row" key={rule}>
        <input
          id={`guard-${rule}`}
          type="checkbox"
          checked={c[rule].enabled}
          onChange={(e) => set(rule, { enabled: e.target.checked })}
        />
        <div className="min-w-0">
          <label htmlFor={`guard-${rule}`}>
            <b>{title}</b>
          </label>
          <p>{text}</p>
          {stop && c[rule].enabled && c.autoStop.enabled && <div className="guard-stop">{stop}</div>}
        </div>
      </li>
    );

  /** Флажок «останавливать сам» внутри правила. */
  const stopBox = (rule: 'spendNoLeads' | 'cplSpike' | 'silence', text: ReactNode) =>
    c && (
      <>
        <input
          id={`guard-stop-${rule}`}
          type="checkbox"
          checked={c[rule].stop}
          onChange={(e) => set(rule, { stop: e.target.checked })}
        />
        <Pause aria-hidden="true" />
        <label htmlFor={`guard-stop-${rule}`}>{text}</label>
      </>
    );

  return (
    <Card title="Страховка">
      <p className="muted" style={{ margin: '0 0 12px', fontSize: 'var(--fs-sm)' }}>
        Правила без ИИ: проверка каждые 10 минут, даже когда агент на паузе или лимит подписки кончился.
        Предупреждают плашкой и в Telegram, а отмеченные{' '}
        <Pause aria-label="паузой" className="guard-inline-ico" /> — ещё и сами ставят рекламу на паузу.
        Остановленное страховкой вы включаете в «Решениях», агент — только после паузы и с объяснением.
      </p>
      {c && (
        <div className="guard-auto">
          <input
            id="guard-autostop"
            type="checkbox"
            checked={c.autoStop.enabled}
            onChange={(e) => set('autoStop', { enabled: e.target.checked })}
          />
          <div className="min-w-0">
            <label htmlFor="guard-autostop">
              <b>Автостоп</b>
            </label>
            <p>
              Агент может включить остановленное не раньше чем через{' '}
              <Num
                label="Часов до того, как агенту можно включить"
                value={c.autoStop.agentAfterHours}
                onSave={(n) => set('autoStop', { agentAfterHours: Math.round(n) })}
                width={4}
              />{' '}
              ч (0 — только вы).
              {writeOn === false && (
                <span className="guard-note">
                  {' '}
                  Сейчас не действует: запись в Meta выключена — включите её в карточке «Бюджет и режим
                  агента».
                </span>
              )}
            </p>
          </div>
        </div>
      )}
      {!c ? (
        <p className="muted">Загружаю…</p>
      ) : (
        <ul className="guard-list">
          {row(
            'spendNoLeads',
            'Расход без лидов',
            <>
              Объявление потратило сегодня не меньше ${' '}
              <Num
                label="Минимальный расход, $"
                value={c.spendNoLeads.minSpend}
                onSave={(n) => set('spendNoLeads', { minSpend: n })}
              />{' '}
              и не меньше{' '}
              <Num
                label="Сколько обычных CPL"
                value={c.spendNoLeads.cplMultiple}
                min={1}
                onSave={(n) => set('spendNoLeads', { cplMultiple: n })}
                width={4}
              />{' '}
              обычных CPL, а лидов нет.
            </>,
            stopBox(
              'spendNoLeads',
              <>
                Останавливать объявление с ${' '}
                <Num
                  label="Остановка: минимальный расход, $"
                  value={c.spendNoLeads.stopMinSpend}
                  onSave={(n) => set('spendNoLeads', { stopMinSpend: n })}
                />{' '}
                и{' '}
                <Num
                  label="Остановка: сколько обычных CPL"
                  value={c.spendNoLeads.stopCplMultiple}
                  min={1}
                  onSave={(n) => set('spendNoLeads', { stopCplMultiple: n })}
                  width={4}
                />{' '}
                обычных CPL
              </>,
            ),
          )}
          {row(
            'cplSpike',
            'Всплеск CPL',
            <>
              CPL кампании сегодня в{' '}
              <Num
                label="Во сколько раз выше"
                value={c.cplSpike.multiple}
                min={1}
                onSave={(n) => set('cplSpike', { multiple: n })}
                width={4}
              />{' '}
              раза выше, чем за неделю, при расходе от ${' '}
              <Num
                label="Минимальный расход, $"
                value={c.cplSpike.minSpend}
                onSave={(n) => set('cplSpike', { minSpend: n })}
              />
              .
            </>,
            stopBox('cplSpike', 'Останавливать кампанию'),
          )}
          {row(
            'cplAboveBreakEven',
            'CPL выше окупаемого',
            <>
              CPL оффера за 3 дня в{' '}
              <Num
                label="Во сколько раз выше окупаемого"
                value={c.cplAboveBreakEven.multiple}
                min={1}
                step={0.1}
                onSave={(n) => set('cplAboveBreakEven', { multiple: n })}
                width={4}
              />{' '}
              раза выше окупаемого (прогнозный апрув × выплата) при расходе от ${' '}
              <Num
                label="Минимальный расход за 3 дня, $"
                value={c.cplAboveBreakEven.minSpend}
                onSave={(n) => set('cplAboveBreakEven', { minSpend: n })}
              />{' '}
              — реклама в минусе. Только предупреждает.
            </>,
          )}
          {row(
            'silence',
            'Тишина',
            <>
              За последние{' '}
              <Num
                label="Часов"
                value={c.silence.hours}
                min={1}
                onSave={(n) => set('silence', { hours: n })}
                width={4}
              />{' '}
              ч потрачено от ${' '}
              <Num
                label="Минимальный расход, $"
                value={c.silence.minSpend}
                onSave={(n) => set('silence', { minSpend: n })}
              />
              , а лидов нет — похоже на сломанный лендинг, форму или сеть.
            </>,
            stopBox('silence', 'Останавливать все кампании, которые тратят сегодня'),
          )}
          {row(
            'approveDrop',
            'Падение апрува',
            <>
              Апрув последних дней ниже{' '}
              <Num
                label="Процент от обычного"
                value={Math.round(c.approveDrop.ratio * 100)}
                min={1}
                onSave={(n) => set('approveDrop', { ratio: Math.min(n, 100) / 100 })}
                width={4}
              />
              % от обычного за месяц (если решений не меньше{' '}
              <Num
                label="Минимум решений"
                value={c.approveDrop.minDecided}
                min={1}
                onSave={(n) => set('approveDrop', { minDecided: n })}
                width={4}
              />
              ).
            </>,
          )}
          {row(
            'disapproved',
            'Отклонённые объявления',
            'Meta отклонила включённое объявление или у него проблемы с показом.',
          )}
        </ul>
      )}
      <h3 className="guard-h">Последние срабатывания</h3>
      {q.data && q.data.events.length === 0 ? (
        <p className="muted guard-empty">
          <ShieldCheck aria-hidden="true" />
          Пока ни одного. Когда правило сработает, оно появится здесь и плашкой вверху страницы.
        </p>
      ) : (
        <ul className="guard-events">
          {q.data?.events.map((e) => (
            <li key={e.id}>
              <div className="min-w-0">
                <b>{e.title}</b>
                <span>
                  {when(e.createdAt)}
                  {e.resolvedAt ? ` · прошло ${when(e.resolvedAt)}` : ''}
                </span>
              </div>
              {e.resolvedAt ? (
                <Chip>Прошло</Chip>
              ) : (
                <Chip tone={e.severity === 'critical' ? 'danger' : 'warning'}>Активно</Chip>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
